import as from 'ansis'
import { Command } from 'commander'
import { z } from 'zod'

import { BaseCollections } from '@scalar-org/entities/database'
import { sdkSchema } from '@scalar-org/entities/sdk'
import { teamSchema } from '@scalar-org/entities/team'
import { namespaceSchema } from '@scalar-org/entities/utility'

import { getAuthData, getDatabaseClient } from '@/helpers'
import { output } from '@/helpers/std-out'

export const ListSdkCommand = () => {
  const cmd = new Command('list')

  cmd.description('List all SDKs for a team namespace')

  // ---------------------------------------------------------------------------
  // Options
  cmd.option('--namespace <namespace>', 'Team namespace')

  cmd.action(async (args) => {
    const { error, data } = z
      .object({
        namespace: namespaceSchema.optional(),
      })
      .safeParse({
        namespace: args.namespace,
      })

    if (error)
      return output
        .error()
        .title('Invalid input')
        .message(error.issues[0].message)
        .exit('error')

    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    const client = await getDatabaseClient()

    const team = await client.getOne({
      collectionPath: BaseCollections.Teams,
      query: {
        uid: auth.teamUid,
      },
      schema: teamSchema,
    })

    if (team.error)
      return output
        .error()
        .title('An error occurred when fetching your team.')
        .message(team.message)
        .exit('error')

    const namespace = data.namespace || team.data.namespaces.at(0)

    if (!namespace) {
      return output
        .error()
        .title('Missing namespace')
        .message(
          'Could not find a valid namespace for your API. Please specify a namespace or contact support.',
        )
        .exit('error')
    }

    const sdks = await client.getMany({
      collectionPath: BaseCollections.Sdks(team.data.uid),
      query: {
        namespace,
      },
      schema: sdkSchema.array(),
    })

    if (sdks.error) return output.error().title(sdks.message).exit('error')

    // Display the list of APIs for a namespace
    output
      .info()
      .table(
        [
          [
            as.underline.bold('Slug'),
            as.underline.bold('Title'),
            as.underline.bold('Version'),
            as.underline.bold('Privacy'),
          ],
          ...sdks.data.map((sdk) => {
            return [
              as.blue(sdk.slug),
              as.blue(sdk.title),
              as.blue(sdk.currentVersion),
              sdk.isPrivate ? as.green('Private') : as.yellow('Public'),
            ]
          }),
        ],
        { padding: 2 },
      )
      .print()
  })

  return cmd
}
